import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell, faQuestionCircle, faChevronDown, faChevronRight,
    faUser, faSignOutAlt, faShieldAlt, faBullseye, faUsers,
    faEnvelope, faChartLine, faLayerGroup, faBuilding,
    faBriefcase, faComments, faPlus, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';
import OrgSwitcher from './OrgSwitcher';

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'SV';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'SV';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getRoleBadge = (roleId) => {
    const r = Number(roleId);
    if (r === 99) return { label: 'Superadmin', cls: 'badge-superadmin' };
    if (r === 98) return { label: 'Org Admin', cls: 'badge-orgadmin' };
    if (r === 99) return { label: 'Manager', cls: 'badge-manager' };
    if (r === 97) return { label: 'Manager', cls: 'badge-manager' };
    return { label: 'Employee', cls: 'badge-employee' };
};

const Layout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, logout, isSuper, isOrgAdmin, isManager, isAdmin, roleId } = useAuth();

    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [moduleSearch, setModuleSearch] = useState('');
    const dropdownRef = useRef(null);

    const isOpportunitiesActive = location.pathname === '/opportunities' || location.pathname.startsWith('/opportunities/');
    const { label: roleLabel, cls: roleCls } = getRoleBadge(roleId);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setProfileDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!currentUser) return null;

    // Filter modules in sidebar based on quick search
    const filterText = moduleSearch.toLowerCase();
    const matches = (label) => !filterText || label.toLowerCase().includes(filterText);

    return (
        <div className="crm-layout dome-layout">
            {/* ── 1. DOME DARK TOPBAR (44px) ── */}
            <header className="global-header dome-topbar">
                <div className="header-left">
                    <button
                        type="button"
                        className="dome-brand-btn"
                        onClick={() => navigate('/dashboard')}
                        title="SalesVault CRM"
                    >
                        <span className="dome-brand-badge">SV</span>
                        <span className="dome-brand-name">SalesVault</span>
                    </button>
                    <span className="topbar-divider">/</span>
                    {/* Organization Switcher */}
                    <OrgSwitcher />
                </div>

                <div className="header-center">
                    <GlobalSearch />
                </div>

                <div className="header-right">

                    {/* User Profile Pill */}
                    <div className="profile-dropdown-container" ref={dropdownRef}>
                        <button
                            type="button"
                            className="topbar-profile-pill"
                            onClick={() => setProfileDropdownOpen(prev => !prev)}
                            title="User Profile &amp; Settings"
                            aria-expanded={profileDropdownOpen}
                            aria-haspopup="true"
                        >
                            <span className="topbar-avatar-circle">
                                {getInitials(currentUser.name)}
                            </span>
                            <span className="topbar-user-name">
                                {currentUser.name}
                            </span>
                            <FontAwesomeIcon icon={faChevronDown} className="icon-mr-xs" />
                        </button>

                        {profileDropdownOpen && (
                            <div className="slds-profile-popover">
                                {/* User Identity Card */}
                                <div className="popover-user-card">
                                    <div className="popover-avatar-wrapper">
                                        <div className="popover-avatar">
                                            <span>{getInitials(currentUser.name)}</span>
                                        </div>
                                        <span className="popover-status-indicator" title="Active"></span>
                                    </div>
                                    <div className="popover-user-meta">
                                        <div className="popover-user-title-row">
                                            <span className="user-name">{currentUser.name}</span>
                                            <span className={`slds-role-pill ${roleCls}`}>
                                                <FontAwesomeIcon icon={isSuper || isOrgAdmin ? faShieldAlt : isManager ? faUsers : faUser} className="icon-mr-4" />
                                                {roleLabel}
                                            </span>
                                        </div>
                                        <div className="user-email">{currentUser.email}</div>
                                    </div>
                                </div>

                                <div className="popover-divider" />

                                {/* Navigation Section */}
                                <div className="popover-menu-section">

                                    <button
                                        type="button"
                                        className="popover-item"
                                        onClick={() => { setProfileDropdownOpen(false); navigate('/dashboard'); }}
                                    >
                                        <div className="popover-icon-box">
                                            <FontAwesomeIcon icon={faChartLine} />
                                        </div>
                                        <div className="item-text">
                                            <span>Dashboard</span>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                    </button>

                                    <button
                                        type="button"
                                        className="popover-item"
                                        onClick={() => { setProfileDropdownOpen(false); navigate('/outreach'); }}
                                    >
                                        <div className="popover-icon-box">
                                            <FontAwesomeIcon icon={faComments} />
                                        </div>
                                        <div className="item-text">
                                            <span>Outreach</span>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                    </button>

                                    <button
                                        type="button"
                                        className="popover-item"
                                        onClick={() => { setProfileDropdownOpen(false); navigate('/targets'); }}
                                    >
                                        <div className="popover-icon-box">
                                            <FontAwesomeIcon icon={faBullseye} />
                                        </div>
                                        <div className="item-text">
                                            <span>Sales Targets &amp; Quotas</span>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                    </button>
                                </div>

                                {isAdmin && (
                                    <>
                                        <div className="popover-divider" />
                                        <div className="popover-menu-section">

                                            {isSuper && (
                                                <button
                                                    type="button"
                                                    className="popover-item"
                                                    onClick={() => { setProfileDropdownOpen(false); navigate('/organizations'); }}
                                                >
                                                    <div className="popover-icon-box">
                                                        <FontAwesomeIcon icon={faLayerGroup} />
                                                    </div>
                                                    <div className="item-text">
                                                        <span>Organizations</span>
                                                    </div>
                                                    <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className="popover-item"
                                                onClick={() => { setProfileDropdownOpen(false); navigate('/users'); }}
                                            >
                                                <div className="popover-icon-box">
                                                    <FontAwesomeIcon icon={faUsers} />
                                                </div>
                                                <div className="item-text">
                                                    <span>User Management</span>
                                                </div>
                                                <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                            </button>

                                            <button
                                                type="button"
                                                className="popover-item"
                                                onClick={() => { setProfileDropdownOpen(false); navigate('/access-control'); }}
                                            >
                                                <div className="popover-icon-box">
                                                    <FontAwesomeIcon icon={faShieldAlt} />
                                                </div>
                                                <div className="item-text">
                                                    <span>Role Access Control</span>
                                                </div>
                                                <FontAwesomeIcon icon={faChevronRight} className="item-arrow" />
                                            </button>
                                        </div>
                                    </>
                                )}

                                <div className="popover-divider" />

                                {/* Footer Actions */}
                                <div className="popover-footer">
                                    <button type="button" className="slds-logout-btn" onClick={logout}>
                                        <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
                                        <span>Log Out</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* ── 2. BODY CONTAINER WITH LEFT SIDEBAR & MAIN CONTENT ── */}
            <div className="crm-body-container">
                {/* ── LEFT SIDEBAR (220px) ── */}
                <aside className="dome-sidebar">

                    {/* Navigation Items */}
                    <nav className="sidebar-nav-list">
                        {matches('Dashboard') && (
                            <NavLink to="/dashboard" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faChartLine} className="sidebar-icon" />
                                <span>Dashboard</span>
                            </NavLink>
                        )}

                        {matches('Accounts') && (
                            <NavLink to="/leads" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faBuilding} className="sidebar-icon" />
                                <span>Accounts</span>
                            </NavLink>
                        )}

                        {matches('Outreach') && (
                            <NavLink to="/outreach" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faComments} className="sidebar-icon" />
                                <span>Outreach</span>
                            </NavLink>
                        )}

                        {matches('Contacts') && (
                            <NavLink to="/contacts" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faUser} className="sidebar-icon" />
                                <span>Contacts</span>
                            </NavLink>
                        )}

                        {matches('Opportunities') && (
                            <NavLink to="/opportunities" className={`sidebar-nav-item ${isOpportunitiesActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faBriefcase} className="sidebar-icon" />
                                <span>Opportunities</span>
                            </NavLink>
                        )}

                        {matches('Targets & Quotas') && (
                            <NavLink to="/targets" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                <FontAwesomeIcon icon={faBullseye} className="sidebar-icon" />
                                <span>Targets &amp; Quotas</span>
                            </NavLink>
                        )}

                        {isAdmin && (
                            <>
                                <div className="sidebar-group-title">Admin</div>
                                {isSuper && matches('Organizations') && (
                                    <NavLink to="/organizations" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                        <FontAwesomeIcon icon={faLayerGroup} className="sidebar-icon" />
                                        <span>Organizations</span>
                                    </NavLink>
                                )}
                                {matches('Users') && (
                                    <NavLink to="/users" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                        <FontAwesomeIcon icon={faUsers} className="sidebar-icon" />
                                        <span>Users</span>
                                    </NavLink>
                                )}
                                {matches('Access Control') && (
                                    <NavLink to="/access-control" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                                        <FontAwesomeIcon icon={faShieldAlt} className="sidebar-icon" />
                                        <span>Access Control</span>
                                    </NavLink>
                                )}
                            </>
                        )}
                    </nav>

                    {/* Sidebar Footer */}
                    <div className="sidebar-footer-links">
                        <button
                            type="button"
                            className="sidebar-nav-item"
                            onClick={() => setProfileDropdownOpen(true)}
                        >
                            <FontAwesomeIcon icon={faQuestionCircle} className="sidebar-icon" />
                            <span>Help &amp; Support</span>
                        </button>
                        <button
                            type="button"
                            className="sidebar-nav-item"
                            onClick={logout}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} className="sidebar-icon" />
                            <span>Log Out</span>
                        </button>
                    </div>
                </aside>

                {/* ── MAIN CONTENT ── */}
                <main className="dashboard-main dome-main-content">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
